import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";

export default function ForgotPassword() {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setMessage({ type: 'error', text: 'Bitte geben Sie eine E-Mail-Adresse ein.' });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      await resetPassword(email);
      setMessage({ 
        type: 'success', 
        text: 'Wenn ein Konto mit dieser E-Mail existiert, erhalten Sie eine E-Mail mit weiteren Anweisungen.' 
      });
      setEmail('');
    } catch (error) {
      if (error instanceof Error) {
        setMessage({ type: 'error', text: error.message });
      } else {
        setMessage({ type: 'error', text: 'Ein unbekannter Fehler ist aufgetreten.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left side - illustration */}
      <div className="hidden lg:flex flex-1 bg-primary items-center justify-center p-8">
        <div className="max-w-md text-white">
          <h1 className="text-3xl font-bold mb-6">LeadsPro - Ihr Lead Management System</h1>
          <p className="text-lg mb-4">
            Haben Sie Ihr Passwort vergessen? Kein Problem! Geben Sie Ihre E-Mail-Adresse ein, und wir senden Ihnen einen Link zum Zurücksetzen.
          </p>
          <ul className="space-y-2 mb-6">
            <li className="flex items-center">
              <div className="rounded-full bg-white/20 p-1 mr-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              Schneller und sicherer Passwort-Reset
            </li>
            <li className="flex items-center">
              <div className="rounded-full bg-white/20 p-1 mr-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              Einfach wieder Zugriff auf Ihr Konto erhalten
            </li>
          </ul>
        </div>
      </div>

      {/* Right side - password reset form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold">Passwort zurücksetzen</h2>
            <p className="text-muted-foreground mt-2">
              Geben Sie Ihre E-Mail-Adresse ein, um einen Link zum Zurücksetzen Ihres Passworts zu erhalten
            </p>
          </div>

          {message && (
            <div className={`px-4 py-3 rounded-md text-sm mb-4 ${
              message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-destructive/15 text-destructive'
            }`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="email">E-Mail</Label>
              <Input
                id="email"
                placeholder="name@example.com"
                type="email"
                autoCapitalize="none"
                autoComplete="email"
                autoCorrect="off"
                disabled={isLoading}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <Button disabled={isLoading} type="submit" className="w-full">
              {isLoading && <span className="mr-2">⏳</span>}
              Link zum Zurücksetzen senden
            </Button>
          </form>

          <div className="text-center text-sm">
            <p className="text-muted-foreground">
              Zurück zum{" "}
              <Link to="/login" className="text-primary hover:underline">
                Login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
